<script setup lang="ts">
import { AllExtensions, RequiredExtensions, UIRichTextEditor } from "@gohighlevel/ghl-text-editor";
import { UIContentWrap } from "@gohighlevel/ghl-ui";
import { History } from "@tiptap/extension-history";
// @ts-ignore - types provided by editor build
import { Editor, Extension } from "@tiptap/vue-3";
import { computed, onUnmounted, shallowRef, watch } from "vue";
import FooterStyleMenu from "./FooterStyleMenu.vue";
import StyleMenu from "./StyleMenu.vue";

interface Props {
  modelValue?: string;
  isSubmitMessage?: boolean;
  size?: string | undefined;
  editorHeight?: string;
  customWidth?: string | undefined;
  contentStyle?: Record<string, string> | undefined;
  mobileInline?: boolean | undefined;
  // Teleport target for embed modals; either a selector or an element
  modalContainer?: string | HTMLElement | null | undefined;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  isSubmitMessage: false,
  size: undefined,
  editorHeight: "84px",
  customWidth: undefined,
  contentStyle: undefined,
  mobileInline: undefined,
  modalContainer: undefined,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const resolvedEditorHeight = computed(() => (props.isSubmitMessage ? "120px" : props.editorHeight));

const buildExtensions = (): Extension[] => {
  const extensions: Extension[] = [];
  extensions.push(History);
  extensions.push(
    AllExtensions.configure({
      image: { inline: true },
      placeholder: { placeholder: "", showForAllNodes: false },
      divTag: { inline: false, preserveWhitespace: false },
      preTag: { inline: false, preserveWhitespace: false },
      // Allow per-block styles on more nodes
      textAlignment: { types: ["heading", "paragraph", "blockquote", "listItem"] },
      lineHeight: { types: ["heading", "paragraph", "blockquote", "listItem"] },
      paragraph: { HTMLAttributes: { style: "margin:0px" } },
    })
  );
  extensions.push(RequiredExtensions);
  return extensions;
};

const editor = shallowRef<Editor>(
  new Editor({
    content: props.modelValue || "",
    parseOptions: { preserveWhitespace: "full" },
    editorProps: { attributes: { class: "editor-class-override" } },
    extensions: buildExtensions(),
    onUpdate: ({ editor }) => {
      emit("update:modelValue", editor.getHTML());
    },
  })
);

watch(
  () => props.modelValue,
  (newValue) => {
    if (!editor.value) {
      return;
    }
    const isSame = editor.value.getHTML() === (newValue || "");
    if (isSame) {
      return;
    }
    editor.value.commands.setContent(newValue || "", false);
  }
);

onUnmounted(() => {
  if (editor.value) {
    editor.value.destroy();
  }
});
</script>

<template>
  <div id="richTextEditor" class="mb-2 min-w-0 rounded-lg border-gray-400">
    <UIContentWrap v-if="editor" class="rounded-md border border-gray-300 bg-white">
      <StyleMenu
        :editor="editor"
        :mobile-inline="props.mobileInline === true"
        :is-submit-message="props.isSubmitMessage === true"
        :modal-container="props.modalContainer"
      />
      <div class="min-w-0" :style="props.contentStyle">
        <UIRichTextEditor
          :editor="editor"
          :container-class="`text-black ghl-form-survey-text-editor${
            props.isSubmitMessage ? ' submit-message-editor' : ''
          }`"
        />
      </div>
      <FooterStyleMenu
        :editor="editor"
        :size="props.size"
        :mobile-inline="props.mobileInline === true"
        :is-submit-message="props.isSubmitMessage === true"
      />
    </UIContentWrap>
  </div>
</template>

<style>
#richTextEditor .ProseMirror {
  overflow-wrap: anywhere;
  hyphens: auto;
  border-radius: 6px;
}
#richTextEditor .ProseMirror:focus-visible {
  outline: none !important;
  border: none !important;
}
#richTextEditor .ProseMirror {
  min-height: 84px;
  overflow: auto;
  height: v-bind("resolvedEditorHeight");
}

.ghl-form-survey-text-editor {
  border: 0 !important;
  padding: 0 !important;
  max-width: v-bind(
    "props.customWidth ? props.customWidth : (props.size === 'mid' ? '66rem' : '33rem')"
  );
}
.ghl-form-survey-text-editor .editor-wrapper {
  padding: 8px;
  max-width: v-bind(
    "props.customWidth ? props.customWidth : (props.size === 'mid' ? '66rem' : '21rem')"
  );
}
.ghl-form-survey-text-editor .ProseMirror {
  font-size: 14px;
  height: 12rem; /* fallback height */
  overflow-y: auto;
  overflow-x: auto;
  resize: vertical;
}
.ghl-form-survey-text-editor .ProseMirror img:not([width]) {
  /* Auto-fit newly inserted images unless an explicit width is set */
  width: 100%;
  height: auto;
  max-width: 100%;
  display: block;
}
.ghl-form-survey-text-editor .ProseMirror p {
  margin: 0;
}
</style>

<style scoped>
:deep(.hl-wrapper-container) {
  padding: 0 !important;
  background-color: #ffffff !important;
}

/* Prevent selection from extending outside the editor */
#richTextEditor {
  contain: paint;
}
</style>
